import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Send,
  Component as ComponentIcon,
  Layers,
  Lock,
} from 'lucide-react';
import { api } from '../hooks/api.js';
import { ApplyUpdateModal } from '../components/ApplyUpdateModal.js';
import { useCreateGuard, PlanUpgradeNotice } from '../components/PlanGuard.js';
import type { ComponentTarget } from '@blogforge/shared';

interface ComponentRow {
  id: string;
  name: string;
  description: string | null;
  target: ComponentTarget;
  version: number;
  blocks: string;
  usageCount: number;
  staleUsageCount: number;
  updatedAt: string;
  /** System components (the seeded "System footer") are read-only templates. */
  locked?: boolean;
}

const TARGET_FILTERS: { value: 'all' | ComponentTarget; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'post', label: 'Posts' },
  { value: 'email', label: 'Emails' },
  { value: 'both', label: 'Both' },
];

const TARGET_BADGE: Record<ComponentTarget, string> = {
  post: 'Posts only',
  email: 'Emails only',
  both: 'Posts & emails',
};

/**
 * Locked components ship with BlogForger. The server refuses to update, delete
 * or version-push them (403), so the UI offers none of those actions — but a
 * locked template is still insertable: insertion copies its blocks, and the
 * copy is a normal, fully editable component of the workspace.
 */
const LOCKED_COMPONENT_HINT =
  'Shipped by BlogForger — insert it and edit your copy; the template itself is locked.';

export function ComponentsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [targetFilter, setTargetFilter] = useState<'all' | ComponentTarget>('all');
  const [applyFor, setApplyFor] = useState<{ id: string; name: string } | null>(null);
  const componentGuard = useCreateGuard('component');

  const { data, isLoading } = useQuery({
    queryKey: ['components', { search, target: targetFilter }],
    queryFn: () =>
      api.components.list({
        search: search || undefined,
        target: targetFilter === 'all' ? undefined : targetFilter,
        limit: 50,
      }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.components.delete(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['components'] });
    },
  });

  const rows: ComponentRow[] = (data as { data?: ComponentRow[] } | undefined)?.data ?? [];

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* ── Page header ─────────────────────────────────────── */}
      <header className="relative shrink-0 px-8 pt-8 pb-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="kicker mb-2">
              <span className="bg-accent h-1.5 w-1.5 rounded-full" />
              Reusable library
            </p>
            <h1 className="text-ink text-[22px] font-bold tracking-tight">Components</h1>
            <p className="text-ink-soft mt-1 text-[13px]">
              Save block groups once, insert them anywhere — posts and emails.
            </p>
          </div>
          {componentGuard.allowed ? (
            <button
              onClick={() => navigate('/components/new')}
              type="button"
              className="btn-primary"
              title="Create a new reusable component"
            >
              <Plus size={15} strokeWidth={2.4} />
              New component
            </button>
          ) : (
            <button
              type="button"
              disabled
              title={componentGuard.blockedReason}
              className="btn-primary"
            >
              <Lock size={14} />
              Individual required
            </button>
          )}
        </div>
      </header>

      {/* ── Plan feature notice ───────────────────────────────── */}
      {!componentGuard.allowed && componentGuard.blockedReason && (
        <div className="shrink-0 px-8 pb-4">
          <PlanUpgradeNotice reason={componentGuard.blockedReason} />
        </div>
      )}

      {/* ── Toolbar ─────────────────────────────────────────── */}
      <div className="shrink-0 px-8 pb-5">
        <div className="surface-row flex flex-wrap items-center gap-3 px-3.5 py-3">
          <div className="input-with-icon max-w-md min-w-52 flex-1">
            <Search />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search components…"
              className="field-input bg-bg/60"
            />
          </div>
          <div className="border-line bg-bg/60 ml-auto flex items-center gap-1 rounded-full border p-1">
            {TARGET_FILTERS.map((f) => (
              <button
                key={f.value}
                onClick={() => setTargetFilter(f.value)}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-all duration-150 ${
                  targetFilter === f.value
                    ? 'bg-card text-ink shadow-sm'
                    : 'text-muted hover:text-ink-soft'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Component list ──────────────────────────────────── */}
      <div className="min-h-0 flex-1 overflow-y-auto px-8 pb-10">
        {isLoading ? (
          <div className="text-muted flex h-48 items-center justify-center gap-2 text-sm">
            <span className="border-line-strong border-t-accent h-3.5 w-3.5 animate-spin rounded-full border-2" />
            Loading components…
          </div>
        ) : rows.length === 0 ? (
          <div className="flex h-full min-h-72 flex-col items-center justify-center text-center">
            <div className="bg-accent/10 text-accent mb-5 flex h-16 w-16 items-center justify-center rounded-2xl shadow-[inset_0_0_0_1px_var(--color-accent-border)]">
              <ComponentIcon size={26} strokeWidth={1.8} />
            </div>
            <h2 className="text-ink text-base font-semibold">No components yet</h2>
            <p className="text-muted mx-auto mt-1 max-w-sm text-[13px] leading-relaxed">
              Save reusable blocks from any post or email (“Save as component”), or build one from
              scratch — then insert it anywhere with a couple of clicks.
            </p>
            <button
              onClick={() => navigate('/components/new')}
              type="button"
              className="btn-primary mt-5"
            >
              <Plus size={15} strokeWidth={2.4} />
              Create your first component
            </button>
          </div>
        ) : (
          <div className="border-line bg-card shadow-card overflow-hidden rounded-2xl border">
            <table className="w-full text-left">
              <thead>
                <tr className="border-line bg-bg/40 border-b">
                  <th className="text-muted px-5 py-3 text-[11px] font-semibold tracking-[0.08em] uppercase">
                    Name
                  </th>
                  <th className="text-muted px-4 py-3 text-[11px] font-semibold tracking-[0.08em] uppercase">
                    Use in
                  </th>
                  <th className="text-muted px-4 py-3 text-[11px] font-semibold tracking-[0.08em] uppercase">
                    Blocks
                  </th>
                  <th className="text-muted px-4 py-3 text-[11px] font-semibold tracking-[0.08em] uppercase">
                    Version
                  </th>
                  <th className="text-muted px-4 py-3 text-[11px] font-semibold tracking-[0.08em] uppercase">
                    Usages
                  </th>
                  <th className="text-muted px-4 py-3 text-[11px] font-semibold tracking-[0.08em] uppercase">
                    Updated
                  </th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-line divide-y">
                {rows.map((row) => {
                  let blockCount = 0;
                  try {
                    blockCount = (JSON.parse(row.blocks) as unknown[]).length;
                  } catch {
                    blockCount = 0;
                  }
                  const outdated = row.staleUsageCount > 0;
                  const locked = row.locked === true;
                  // Shared between the linked (editable) and plain (locked) name cells.
                  const nameCell = (
                    <>
                      <span className="bg-accent/10 text-accent flex h-8 w-8 shrink-0 items-center justify-center rounded-lg">
                        {locked ? <Lock size={14} /> : <Layers size={14} />}
                      </span>
                      <span>
                        <span className="flex items-center gap-2">
                          <span
                            className={`text-ink block text-[13px] font-semibold ${
                              locked ? '' : 'group-hover:text-accent transition-colors'
                            }`}
                          >
                            {row.name}
                          </span>
                          {locked && (
                            <span className="bg-accent/10 text-accent inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold tracking-wide uppercase">
                              <Lock size={9} strokeWidth={2.6} />
                              System
                            </span>
                          )}
                        </span>
                        {row.description && (
                          <span className="text-muted mt-0.5 block max-w-xs truncate text-[11.5px]">
                            {row.description}
                          </span>
                        )}
                        {locked && (
                          <span className="text-muted mt-0.5 block max-w-sm text-[11.5px] leading-snug">
                            {LOCKED_COMPONENT_HINT}
                          </span>
                        )}
                      </span>
                    </>
                  );
                  return (
                    <tr key={row.id} className="group hover:bg-ink/[0.03] transition-colors">
                      <td className="px-5 py-3.5">
                        {locked ? (
                          // Locked: there is no edit destination, so the name is not a link.
                          <span
                            className="flex items-center gap-3 text-left"
                            title={LOCKED_COMPONENT_HINT}
                          >
                            {nameCell}
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => navigate(`/components/${row.id}/edit`)}
                            className="flex items-center gap-3 text-left"
                            title="Edit component"
                          >
                            {nameCell}
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-3.5">
                        <span className="chip px-2 py-0.5 text-[10.5px] tracking-wide uppercase">
                          {TARGET_BADGE[row.target]}
                        </span>
                      </td>
                      <td className="text-ink-soft px-4 py-3.5 text-xs">{blockCount}</td>
                      <td className="px-4 py-3.5">
                        <span className="text-ink-soft font-mono text-xs">v{row.version}</span>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className="text-ink-soft text-xs font-medium">{row.usageCount}</span>
                        {/* "Outdated" only means something while an update can be pushed. */}
                        {outdated && !locked && (
                          <span className="bg-warn/10 text-warn ml-1.5 rounded-full px-2 py-0.5 text-[10px] font-semibold">
                            {row.staleUsageCount} outdated
                          </span>
                        )}
                      </td>
                      <td className="text-muted px-4 py-3.5 text-xs">
                        {new Date(row.updatedAt).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3.5 text-right">
                        {locked ? (
                          // No edit, no delete, no push-update: the server refuses
                          // all three for a locked component. Inserting still works,
                          // because insertion copies the blocks.
                          <span
                            className="text-muted inline-flex items-center gap-1.5 text-[11px]"
                            title={LOCKED_COMPONENT_HINT}
                          >
                            <Lock size={11} />
                            Insert a copy to edit
                          </span>
                        ) : (
                          <div className="flex justify-end gap-1 opacity-0 transition-opacity duration-150 group-hover:opacity-100">
                            {row.usageCount > 0 && (
                              <button
                                type="button"
                                onClick={() => setApplyFor({ id: row.id, name: row.name })}
                                className="btn-ghost px-2.5 text-[11px]"
                                title="Push the latest version into documents using it"
                              >
                                <Send size={12} /> Update docs
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => navigate(`/components/${row.id}/edit`)}
                              className="btn-ghost px-2.5 text-[11px]"
                              title="Edit component"
                            >
                              <Pencil size={12} />
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                // eslint-disable-next-line no-restricted-globals
                                if (
                                  confirm(
                                    `Delete component "${row.name}"? Documents using it keep their snapshots.`,
                                  )
                                ) {
                                  deleteMutation.mutate(row.id);
                                }
                              }}
                              className="btn-ghost text-danger hover:bg-danger/10 hover:text-danger px-2.5 text-[11px]"
                              title="Delete component"
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {applyFor && (
        <ApplyUpdateModal
          componentId={applyFor.id}
          componentName={applyFor.name}
          onClose={() => setApplyFor(null)}
        />
      )}
    </div>
  );
}
